/**
 * Special-User permission helpers.
 *
 * The Uplift has TWO classes of admin:
 *   • full admin    — `user.role === 'admin'`. Unrestricted.
 *   • special user  — any user provisioned via the admin Create-User
 *                     dialog (carries `created_by_admin`) who has been
 *                     granted one or more `admin_*` toggles.
 *
 * Special users see admin pages, but their privileges INSIDE each
 * page are scoped per the spec the product owner laid out. Every UI
 * action across the codebase should consult these helpers instead of
 * re-checking `role === 'admin'` directly.
 */

const isFullAdmin = (user) => user?.role === 'admin';

const isSpecialUser = (user) => !!user?.created_by_admin && user?.role !== 'admin';

const hasToggle = (user, key) =>
  isFullAdmin(user) || (user?.feature_toggles || {})[key] === true;

// Generic "can see this admin nav/page at all"
export const canAccess = (user, key) => hasToggle(user, key);

// User Administration — see/search, edit basic+contact, lock non-admins
export const userAdmin = (user) => ({
  view: hasToggle(user, 'admin_user_administration'),
  createUser: isFullAdmin(user),
  editBasic: hasToggle(user, 'admin_user_administration'),
  editContact: hasToggle(user, 'admin_user_administration'),
  editMetrics: isFullAdmin(user),
  lockTarget: (target) => {
    if (isFullAdmin(user)) return true;
    if (!hasToggle(user, 'admin_user_administration')) return false;
    // Special users CANNOT lock other admins or special users
    if (target?.role === 'admin') return false;
    if (target?.created_by_admin) return false;
    return true;
  },
  alterFeatureAccess: isFullAdmin(user),
  deleteUser: isFullAdmin(user),
});

// Onboarding Management — view process + candidates, no edits, no admin-creations tab
export const onboardingAdmin = (user) => ({
  view: hasToggle(user, 'admin_onboarding'),
  viewProcess: hasToggle(user, 'admin_onboarding'),
  viewCandidates: hasToggle(user, 'admin_onboarding'),
  editCandidate: isFullAdmin(user),
  viewAdminCreations: isFullAdmin(user),
});

// Assignments — full reassign / move-to-pool privileges for special users too
export const assignments = (user) => ({
  view: hasToggle(user, 'admin_assignments'),
  reassign: hasToggle(user, 'admin_assignments'),
  moveToPool: hasToggle(user, 'admin_assignments'),
});

// User Communications
export const userCommunications = (user) => ({
  view: hasToggle(user, 'admin_user_communications'),
  search: hasToggle(user, 'admin_user_communications'),
  summarize: hasToggle(user, 'admin_user_communications'),
});

// Calling Admin
export const callingAdmin = (user) => ({
  view: hasToggle(user, 'admin_calling'),
  editHoldMusic: isFullAdmin(user),
  editVoicemailGreeting: isFullAdmin(user),
  editPleaseHoldAudio: isFullAdmin(user),
  editStartDisclaimer: isFullAdmin(user),
  editPlayOnConnect: isFullAdmin(user),
});

// Phone Routing — three independent sub-permissions (Standard / Exemption / Restricted).
// Back-compat: a user still carrying the old single `admin_phone_routing`
// toggle is treated as having all three.
export const phoneRouting = (user) => {
  const adm = isFullAdmin(user);
  const legacy = !!(user?.feature_toggles || {})['admin_phone_routing'];
  const standard = adm || legacy || hasToggle(user, 'admin_phone_routing_standard');
  const exemption = adm || legacy || hasToggle(user, 'admin_phone_routing_exemption');
  const restricted = adm || legacy || hasToggle(user, 'admin_phone_routing_restricted');
  return {
    view: standard || exemption || restricted,
    standard,
    exemption,
    restricted,
    editRoutingForState: (stateMeta) => {
      if (adm) return true;
      if (stateMeta?.is_restricted) return restricted;
      return standard;
    },
  };
};

// Email Monitor / Queue
export const emailAdmin = (user) => ({
  monitorAllStates: hasToggle(user, 'admin_email_monitor'),
  queueAllStates: hasToggle(user, 'admin_email_queue'),
});

// Statewide Details — Spec: CANNOT alter standard state details, CANNOT
// create exemption for standard state users. So `admin_statewide_details`
// grants VIEW-ONLY access to standard states. To edit a state the
// special user needs `admin_restricted_states` AND the row must be
// restricted. Standard-state edits remain a full-admin-only operation.
export const statewideDetails = (user) => ({
  view: hasToggle(user, 'admin_statewide_details') || hasToggle(user, 'admin_restricted_states'),
  editStateDetails: (stateMeta) => {
    if (isFullAdmin(user)) return true;
    if (stateMeta?.is_restricted) return hasToggle(user, 'admin_restricted_states');
    return false; // standard-state edits = full-admin only
  },
  createExemption: (stateMeta) => {
    if (isFullAdmin(user)) return true;
    if (stateMeta?.is_restricted) return hasToggle(user, 'admin_restricted_states');
    return false; // standard-state exemptions = full-admin only
  },
});

// Restricted States
export const restrictedStates = (user) => ({
  view: hasToggle(user, 'admin_restricted_states'),
  editAny: hasToggle(user, 'admin_restricted_states'),
  createExemption: hasToggle(user, 'admin_restricted_states'),
});

// ORB Universes
export const orbUniverses = (user) => ({
  view: hasToggle(user, 'admin_orb_universes'),
  edit: isFullAdmin(user),
});

// AI Training Hub / Voice Agent (training + dashboard)
export const aiTraining = (user) => ({
  viewHub: hasToggle(user, 'admin_ai_training'),
  viewVoiceAgent: hasToggle(user, 'admin_ai_voice_agent'),
  viewVoiceDashboard: hasToggle(user, 'admin_ai_voice_dashboard'),
  edit: isFullAdmin(user),
});

export const isSpecial = isSpecialUser;
export const isAdmin = isFullAdmin;
